import { makeAutoObservable, runInAction } from "mobx";

import { scheduleImmediatePoll } from "@/store/AddedPropertyReportsWatcherSync";
import { propertyKey } from "@/store/propertyCore/propertyCacheUtils";
import { propertyHasCompsArtifact, propertyHasQmaArtifact } from "@/utils/propertyReportPresenceUtils";

const STORAGE_KEY = "addedPropertyReportsWatcher:v1";

/** Stop polling rows older than this (backend may never complete). */
export const ADDED_PROPERTY_REPORTS_MAX_ENTRY_AGE_MS = 12 * 60 * 1000;

/**
 * @returns {Map<string, { qma: boolean, comps: boolean, createdAt: number, customer: string, propertyId: string }>}
 */
function loadPersistedEntries() {
  if (typeof sessionStorage === "undefined") return new Map();
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const data = JSON.parse(raw);
    if (!data || typeof data !== "object") return new Map();
    const entries = data.entries;
    if (!entries || typeof entries !== "object") return new Map();
    const map = new Map();
    for (const [key, value] of Object.entries(entries)) {
      if (typeof key !== "string" || !value || typeof value !== "object") continue;
      const qma = Boolean(value.qma);
      const comps = Boolean(value.comps);
      const createdAt = Number(value.createdAt);
      const customer = typeof value.customer === "string" ? value.customer.trim() : "";
      const propertyId = typeof value.propertyId === "string" ? value.propertyId.trim() : "";
      if (!customer || !propertyId) continue;
      if (!Number.isFinite(createdAt)) continue;
      map.set(key, { qma, comps, createdAt, customer, propertyId });
    }
    return map;
  } catch {
    return new Map();
  }
}

/**
 * @param {Map<string, object>} map
 */
function persistEntries(map) {
  if (typeof sessionStorage === "undefined") return;
  try {
    const entries = Object.fromEntries(map);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ v: 1, entries }));
  } catch {
    // Quota or private mode — watcher still works in-memory for session.
  }
}

/**
 * Tracks post–add-property backend QMA + comps generation; sessionStorage-backed.
 */
class AddedPropertyReportsWatcherStore {
  /** @type {Map<string, { qma: boolean, comps: boolean, createdAt: number, customer: string, propertyId: string }>} */
  entries = new Map();

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    runInAction(() => {
      this.entries = loadPersistedEntries();
    });
  }

  _persist() {
    persistEntries(this.entries);
  }

  resetForCustomerChange() {
    runInAction(() => {
      this.entries.clear();
    });
    this._persist();
  }

  reset() {
    runInAction(() => {
      this.entries.clear();
    });
    this._persist();
  }

  /**
   * @param {string} customer
   * @param {string[]} propertyIds
   */
  register(customer, propertyIds) {
    const c = String(customer ?? "").trim();
    if (!c || !Array.isArray(propertyIds)) return;

    runInAction(() => {
      for (const pid of propertyIds) {
        const id = String(pid ?? "").trim();
        if (!id) continue;
        const key = propertyKey(c, id);
        if (!this.entries.has(key)) {
          this.entries.set(key, {
            qma: false,
            comps: false,
            createdAt: Date.now(),
            customer: c,
            propertyId: id,
          });
        }
      }
    });
    this._persist();
    scheduleImmediatePoll();
  }

  /**
   * Removes watch rows (e.g. duplicate add or stale sessionStorage from a prior attempt).
   *
   * @param {string} customer
   * @param {string[]} propertyIds
   */
  unregister(customer, propertyIds) {
    const c = String(customer ?? "").trim();
    if (!c || !Array.isArray(propertyIds)) return;

    let changed = false;
    runInAction(() => {
      for (const pid of propertyIds) {
        const id = String(pid ?? "").trim();
        if (!id) continue;
        const key = propertyKey(c, id);
        if (this.entries.delete(key)) {
          changed = true;
        }
      }
    });
    if (changed) {
      this._persist();
    }
  }

  /**
   * @param {string} customer
   * @returns {string[]}
   */
  getWatchedPropertyIdsForCustomer(customer) {
    const c = String(customer ?? "").trim();
    if (!c) return [];
    const ids = [];
    for (const [, row] of this.entries) {
      if (row.customer === c) {
        ids.push(row.propertyId);
      }
    }
    return ids;
  }

  /**
   * @param {string|null|undefined} customer
   * @param {string|null|undefined} propertyId
   * @param {object|null|undefined} property
   * @returns {boolean}
   */
  isQmaPendingForProperty(customer, propertyId, property) {
    if (!customer || !propertyId) return false;
    if (propertyHasQmaArtifact(property)) return false;
    const key = propertyKey(customer, propertyId);
    const row = this.entries.get(key);
    if (!row || row.qma) return false;
    // Comps landed without QMA ⇒ auto pipeline failed/timed out. Stop the card spinner
    // so the user can click Generate Report instead of waiting until the watch row ages out.
    if (row.comps && propertyHasCompsArtifact(property)) return false;
    return true;
  }

  /**
   * @param {string|null|undefined} customer
   * @param {string|null|undefined} propertyId
   * @param {object|null|undefined} property
   * @returns {boolean}
   */
  isCompsPendingForProperty(customer, propertyId, property) {
    if (!customer || !propertyId) return false;
    if (propertyHasCompsArtifact(property)) return false;
    const key = propertyKey(customer, propertyId);
    const row = this.entries.get(key);
    if (!row || row.comps) return false;
    return true;
  }

  /**
   * Comps finished on a watched add-property row but QMA never landed — treat QMA as failed.
   *
   * @param {string|null|undefined} customer
   * @param {string|null|undefined} propertyId
   * @param {object|null|undefined} property
   * @returns {boolean}
   */
  isBackendQmaFailedForProperty(customer, propertyId, property) {
    if (!customer || !propertyId) return false;
    if (propertyHasQmaArtifact(property)) return false;
    const key = propertyKey(customer, propertyId);
    const row = this.entries.get(key);
    if (!row || row.qma) return false;
    return Boolean(row.comps && propertyHasCompsArtifact(property));
  }

  /**
   * Reconcile a single watch row from an in-memory property snapshot (e.g. PropertyDetail after
   * load/subscription). No-ops when there is no entry for the key or the snapshot is unusable.
   *
   * @param {string|null|undefined} customer
   * @param {string|null|undefined} propertyId
   * @param {object|null|undefined} property
   * @returns {{ qmaAnnounced: boolean, compsAnnounced: boolean }}
   */
  reconcileFromPropertyIfWatching(customer, propertyId, property) {
    const c = String(customer ?? "").trim();
    const id = String(propertyId ?? "").trim();
    if (!c || !id || !property || typeof property !== "object") {
      return { qmaAnnounced: false, compsAnnounced: false };
    }
    const key = propertyKey(c, id);
    if (!this.entries.has(key)) {
      return { qmaAnnounced: false, compsAnnounced: false };
    }
    return this.reconcileFromFetchedProperty(c, id, property);
  }

  /**
   * @param {string} customer
   * @param {string} propertyId
   * @param {object|null|undefined} fetchedProperty
   * @returns {{ qmaAnnounced: boolean, compsAnnounced: boolean }}
   */
  reconcileFromFetchedProperty(customer, propertyId, fetchedProperty) {
    const key = propertyKey(customer, propertyId);
    const row = this.entries.get(key);
    if (!row) {
      return { qmaAnnounced: false, compsAnnounced: false };
    }

    const hasQma = propertyHasQmaArtifact(fetchedProperty);
    const hasComps = propertyHasCompsArtifact(fetchedProperty);

    let qmaAnnounced = false;
    let compsAnnounced = false;

    runInAction(() => {
      const current = this.entries.get(key);
      if (!current) return;

      const nextQma = hasQma ? true : current.qma;
      const nextComps = hasComps ? true : current.comps;

      if (hasQma && !current.qma) {
        qmaAnnounced = true;
      }
      if (hasComps && !current.comps) {
        compsAnnounced = true;
      }

      if (nextQma && nextComps) {
        this.entries.delete(key);
      } else {
        this.entries.set(key, {
          ...current,
          qma: nextQma,
          comps: nextComps,
        });
      }
    });

    this._persist();
    return { qmaAnnounced, compsAnnounced };
  }

  /**
   * @param {number} [maxAgeMs]
   */
  pruneExpired(maxAgeMs = ADDED_PROPERTY_REPORTS_MAX_ENTRY_AGE_MS) {
    const now = Date.now();
    let changed = false;
    runInAction(() => {
      for (const [key, row] of this.entries) {
        if (now - row.createdAt > maxAgeMs) {
          this.entries.delete(key);
          changed = true;
        }
      }
    });
    if (changed) {
      this._persist();
    }
  }
}

export default AddedPropertyReportsWatcherStore;
